import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, updateQty } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const subtotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
  const shipping = subtotal >= 5000 ? 0 : (items.length ? 99 : 0);
  const tax = Math.round(subtotal * 0.18);
  const total = subtotal - 0 + shipping + tax;

  if (!user) return (
    <div className="max-w-3xl mx-auto p-14 text-center">
      <ShoppingBag className="w-12 h-12 text-slate-400 mx-auto mb-4" />
      <h2 className="font-heading text-2xl font-semibold">Please sign in to view your cart</h2>
      <Link to="/login" className="mt-6 inline-block bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-xl">Sign in</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-950 mb-8">Your Cart</h1>
      {items.length === 0 ? (
        <div className="text-center py-20 border border-slate-200 rounded-2xl">
          <ShoppingBag className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-6">Your cart is empty</p>
          <Link to="/products" className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-xl">Shop products</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.product_id} className="bg-white border border-slate-200 rounded-2xl p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                <img src={it.product.image} alt="" className="w-24 h-24 rounded-xl object-cover bg-slate-50" />
                <div className="flex-1">
                  <Link to={`/product/${it.product_id}`} className="font-heading font-semibold text-slate-900 hover:text-blue-900 line-clamp-2">{it.product.name}</Link>
                  <div className="text-xs text-slate-500 mt-1">{it.product.brand} · {it.product.category}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border border-slate-200 rounded-lg">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2" data-testid={`cart-dec-${it.product_id}`}><Minus className="w-3.5 h-3.5" /></button>
                      <div className="px-4 text-sm">{it.quantity}</div>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2" data-testid={`cart-inc-${it.product_id}`}><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <button onClick={() => updateQty(it.product_id, 0)} className="text-slate-500 hover:text-red-600" data-testid={`cart-remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg font-bold text-slate-950">₹{(it.product.price * it.quantity).toLocaleString("en-IN")}</div>
                  <div className="text-xs text-slate-400">₹{it.product.price.toLocaleString("en-IN")} each</div>
                </div>
              </div>
            ))}
          </div>
          <aside className="bg-slate-50 border border-slate-200 rounded-2xl p-6 h-fit sticky top-24">
            <div className="font-heading text-lg font-semibold mb-4">Order Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-medium">₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span className="font-medium">{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">GST (18%)</span><span className="font-medium">₹{tax.toLocaleString("en-IN")}</span></div>
            </div>
            <div className="border-t border-slate-200 my-4"></div>
            <div className="flex justify-between font-heading font-bold text-lg mb-6"><span>Total</span><span>₹{total.toLocaleString("en-IN")}</span></div>
            <button onClick={() => nav("/checkout")} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3.5 rounded-xl font-medium" data-testid="cart-checkout-btn">
              Proceed to Checkout
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
